import type { MouseEvent } from 'react';
import { SkeletonType } from '@/components/Table/TableRowSkeleton';
import { type TableEmptyStateConfig } from '@/components/Table/TableEmptyState';

export type { TableEmptyStateConfig };

export enum SortOrder {
    ASC = 'asc',
    DESC = 'desc',
}

export interface ColumnEditConfig {
    initialValue?: string;
    displayValue?: string;
}

export interface TableCellContext<TData> {
    row: { original: TData };
    value: unknown;
    isExpanded?: boolean;
    toggleExpand?: () => void;
}

export interface TableColumn<TData> {
    header: string | React.ReactNode;
    accessor?: keyof TData;
    id?: string;
    cell?: (props: TableCellContext<TData>) => React.ReactNode;
    sortType?: 'string' | 'number' | 'date';
    enableSorting?: boolean;
    enableSearch?: boolean;
    width?: string | number;
    minWidth?: string | number;
    maxWidth?: string | number;
    editable?: boolean;
    editConfig?: ColumnEditConfig;
    interactive?: boolean;
}

export interface TableAction<TData> {
    label: string;
    onClick: (row: TData) => void | Promise<void>;
    icon?: React.ReactNode;
    variant?: 'default' | 'danger';
    show?: boolean;
}

export interface TableProps<TData> {
    data: TData[];
    columns: TableColumn<TData>[];
    actions?: (row: TData) => TableAction<TData>[];
    isLoading?: boolean;
    isFetching?: boolean;
    page?: number;
    pageSize?: number;
    totalCount?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: SortOrder;
    skeletonRowCount?: number;
    skeletonTypeMap?: Record<string, SkeletonType>;
    onLoadMore?: () => void;
    onPageChange?: (page: number) => void;
    onRowClick?: (row: TData, event?: MouseEvent<HTMLTableRowElement>) => void;
    onSearchChange?: (search: string) => void;
    onSortChange?: (sortBy: string, sortOrder: SortOrder) => void;
    inlineEditConfig?: InlineEditConfig<TData>;
    emptyState?: TableEmptyStateConfig;
    resizableColumns?: boolean;
    expandableRow?: (row: TData, isExpanded: boolean) => React.ReactNode;
}

export interface CellAddress {
    rowId: string;
    columnId: string;
}

export interface CellChange<TData> {
    rowId: string;
    columnId: string;
    accessor: keyof TData;
    originalValue: unknown;
    newValue: unknown;
    row: TData;
}

export interface InlineEditConfig<TData> {
    getRowId: (row: TData) => string;
    editingCell: CellAddress | null;
    pendingChanges: Map<string, CellChange<TData>>;
    onEditStart: (cell: CellAddress) => void;
    onEditCommit: (change: CellChange<TData>) => void;
    onEditCancel: () => void;
}

export interface UseTableEditOptions<TData> {
    getRowId: (row: TData) => string;
    onSave?: (changes: CellChange<TData>[]) => Promise<void>;
}

export interface UseTableEditReturn<TData> {
    config: InlineEditConfig<TData>;
    isDirty: boolean;
    isSaving: boolean;
    save: () => Promise<void>;
    discard: () => void;
    changes: CellChange<TData>[];
}

export interface TableStateConfig {
    initialPage?: number;
    initialPageSize?: number;
    initialSearch?: string;
    initialSort?: { by: string; order: SortOrder };
    debounceMs?: number;
}
